import { INSTALLMENT_COUNTS, INSTALLMENT_INTERVALS, INVOICE_STATE_LABEL, PAY_SCHEDULE_MSG, installmentPlanLabel, intervalLabel, overdueText, type InstallmentChoice, type InstallmentPlan, type Invoice, type InvoiceState, type Partner, type PartnerReceivables, type SchedulePreview } from "@genbaryoku/contract";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../../api.js";
import { Help } from "../../components/ui.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { useState } from "react";
import { t, useLabels } from "../../labels.js";
import { mdSlash, yen } from "../../lib/num.js";

/**
 * 分割払い（#267 3b、docs/97 決めごと10〜12、Figma 279:115）。差し込み口 invoice.schedule・partner.receivables（sales の会社だけ）。
 *   回ごとの残は API（DB の v_payment_schedule_balance＝入金を期日の古い順に当てたとみなす）。画面は足し算だけ
 */

const ymdSlash = (d: string | null) => (d ? d.replace(/-/g, "/") : PAY_SCHEDULE_MSG.noDue);

/** 回の状態のチップ（色だけで伝えない＝文字。朱は期限超過だけ） */
export function ScheduleStateChip({ state, overdueDays }: { state: InvoiceState; overdueDays: number }) {
  if (state === "overdue") {
    return (
      <span className="chip shu" data-testid="schedule-state" title={overdueText(overdueDays)}>
        {INVOICE_STATE_LABEL.overdue}
      </span>
    );
  }
  return (
    <span className={"chip" + (state === "paid" || state === "partial" ? " ai" : " quiet")} data-testid="schedule-state">
      {INVOICE_STATE_LABEL[state]}
    </span>
  );
}

/** 請求書の画面の「お支払い予定」（出した分割払いの請求書だけ）：回・お支払期限・金額・入金・残・状態、下に合計 */
export function InvoiceScheduleCard({ invoice }: { invoice: Invoice }) {
  useLabels();
  const sc = invoice.payments?.schedule;
  if (!sc) return null;
  const total = sc.rows.reduce((a, r) => a + r.amount, 0);
  const paid = sc.rows.reduce((a, r) => a + r.paid, 0);
  const balance = sc.rows.reduce((a, r) => a + r.balance, 0);
  return (
    <section className="psched" data-testid="invoice-schedule">
      <div className="psched-h">
        <h3 className="psched-t">
          {PAY_SCHEDULE_MSG.schedule}
          <Help testId="help-schedule" label={PAY_SCHEDULE_MSG.schedule} text={t(PAY_SCHEDULE_MSG.scheduleHelp)} />
        </h3>
        <span className="sp" />
        <span className="muted" data-testid="invoice-schedule-plan">{PAY_SCHEDULE_MSG.planOf(sc.count, sc.intervalMonths)}</span>
      </div>
      <div className="psched-scroll">
      <table className="list psched-table">
        <thead>
          <tr>
            <th>{PAY_SCHEDULE_MSG.colSeq}</th>
            <th>{PAY_SCHEDULE_MSG.colDue}</th>
            <th className="r">{PAY_SCHEDULE_MSG.colAmount}</th>
            <th className="r">{PAY_SCHEDULE_MSG.colPaid}</th>
            <th className="r">{PAY_SCHEDULE_MSG.colBalance}</th>
            <th aria-label="状態" />
          </tr>
        </thead>
        <tbody>
          {sc.rows.map((r) => (
            <tr key={r.seq} data-testid="invoice-schedule-row">
              <td>{PAY_SCHEDULE_MSG.seqLabel(r.seq)}</td>
              <td className="num">{ymdSlash(r.dueOn)}</td>
              <td className="num r">{yen(r.amount)}</td>
              <td className="num r">{yen(r.paid)}</td>
              <td className={"num r" + (r.balance > 0 ? " b" : "")}>{yen(r.balance)}</td>
              <td>
                <ScheduleStateChip state={r.state} overdueDays={r.overdueDays} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="psched-foot" data-testid="invoice-schedule-foot">
        <span className="muted">{PAY_SCHEDULE_MSG.total}</span> <span className="num">{yen(total)}</span> 円
        <span className="muted">{PAY_SCHEDULE_MSG.colPaid}</span> <span className="num">{yen(paid)}</span> 円
        <span className="muted">{PAY_SCHEDULE_MSG.colBalance}</span> <b className="num">{yen(balance)}</b> 円
      </div>
    </section>
  );
}

/** 入金予定の問い合わせ（{customer}の画面が先読みする＝差し込み口 partner.receivables の preload） */
export const receivablesQuery = (partnerId: string) =>
  queryOptions({ queryKey: ["partner-receivables", partnerId], queryFn: () => api<PartnerReceivables>("GET", `/partners/${partnerId}/receivables`) });

/** {customer}の画面の「入金予定」：期日の月ごとの合計と内訳（請求書・回・期日・金額・残・状態） */
export function PartnerReceivablesSection({ partner }: { partner: Partner }) {
  useLabels();
  const q = useQuery(receivablesQuery(partner.id));
  const d = q.data;
  // 入金予定が無い{customer}（請求書が無い・残も今月からの予定も無い）には節を出さない（先読みしてから描く＝下の節はずれない）
  if (d && d.months.length === 0) return null;
  return (
    <section className="paper pdpay" data-testid="partner-receivables">
      <h2 className="pdpay-t">
        {PAY_SCHEDULE_MSG.receivables}
        <Help testId="help-partner-receivables" label={PAY_SCHEDULE_MSG.receivables} text={t(PAY_SCHEDULE_MSG.receivablesHelp)} />
      </h2>
      {!d ? (
        <div className="psched-wait" aria-hidden="true" />
      ) : (
        <>
          <div className="pdpay-sum">
            <span>
              <span className="muted">{PAY_SCHEDULE_MSG.colBalance}</span> <b className="num">{yen(d.balance)}</b> 円
            </span>
            {d.overdueBalance ? (
              <span className="shu">
                {INVOICE_STATE_LABEL.overdue} <span className="num">{yen(d.overdueBalance)}</span> 円
              </span>
            ) : null}
          </div>
          <div className="psched-scroll">
          <table className="list psched-table">
            <thead>
              <tr>
                <th>{PAY_SCHEDULE_MSG.colDue}</th>
                <th>請求書</th>
                <th>{PAY_SCHEDULE_MSG.colSeq}</th>
                <th className="r">{PAY_SCHEDULE_MSG.colAmount}</th>
                <th className="r">{PAY_SCHEDULE_MSG.colBalance}</th>
                <th aria-label="状態" />
              </tr>
            </thead>
            {d.months.map((m) => (
              <tbody key={m.month || "none"} data-testid="receivables-month" data-month={m.month}>
                <tr className="psched-month">
                  <th colSpan={3} scope="rowgroup">{PAY_SCHEDULE_MSG.monthLabel(m.month)}</th>
                  <td className="num r" data-testid="receivables-month-amount">{yen(m.amount)}</td>
                  <td className="num r b">{yen(m.balance)}</td>
                  <td />
                </tr>
                {m.rows.map((r) => (
                  <tr key={`${r.documentId}-${r.seq}`} data-testid="receivables-row">
                    <td className="num">{r.dueOn ? mdSlash(r.dueOn) : PAY_SCHEDULE_MSG.noDue}</td>
                    <td>
                      <Link to={`/invoices/${r.documentId}`}>{r.documentNo}</Link>
                      <span className="muted psched-subj" title={r.subject}>{r.subject}</span>
                    </td>
                    <td>{r.count > 1 ? PAY_SCHEDULE_MSG.seqOf(r.seq, r.count) : ""}</td>
                    <td className="num r">{yen(r.amount)}</td>
                    <td className={"num r" + (r.balance > 0 ? " b" : "")}>{yen(r.balance)}</td>
                    <td>
                      <ScheduleStateChip state={r.state} overdueDays={r.overdueDays} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * 発行前の確認の「分割払い」（Figma 279:53）：{customer}の設定（3回 毎月）｜指定｜なし。指定なら 回数・間隔。下に回の表（期日と額は API＝DB の fn_payment_schedule_plan）。
 *   分割払いの無い{customer}・選択が「なし」でも、選択の段は出す（1枚だけ分割払いにできる）。回が1つのときは表を出さない
 */
export function IssueInstallments({ invoice, value, onChange }: { invoice: Invoice; value: InstallmentChoice; onChange: (v: InstallmentChoice) => void }) {
  useLabels();
  const client = invoice.paySchedule?.client ?? null;
  // {customer}に分割払いが無いときは「なし｜指定」の2つ（「{customer}の設定（なし）」と「なし」を並べない）＝なしは{customer}に従う
  const mode: "client" | "custom" | "none" = typeof value === "object" ? "custom" : !client ? "client" : value;
  const [plan, setPlan] = useState<InstallmentPlan>(typeof value === "object" ? value : (client ?? { count: 3, intervalMonths: 1 }));
  const q = useQuery({
    queryKey: ["schedule-preview", invoice.id, invoice.updatedAt, value],
    queryFn: () => api<SchedulePreview>("POST", `/invoices/${invoice.id}/schedule-preview`, { installments: value }),
    placeholderData: (prev) => prev,
  });
  const set = (m: "client" | "custom" | "none") => onChange(m === "custom" ? plan : m);
  const setP = (p: InstallmentPlan) => {
    setPlan(p);
    onChange(p);
  };
  const sel = (id: string, label: string, v: number, opts: readonly number[], fmt: (n: number) => string, on: (n: number) => void) => (
    <label className="row" style={{ gap: 6, alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-15)", color: "var(--ink2)" }}>{label}</span>
      <select id={id} className="inp termsel" value={v} onChange={(e) => on(Number(e.target.value))} data-testid={id}>
        {opts.map((o) => (
          <option key={o} value={o}>{fmt(o)}</option>
        ))}
      </select>
    </label>
  );
  // 回の数は選択から分かる＝届く前から同じ行数を取る（#179：届いてから「発行」のボタンを下にずらさない）。合計が回数より小さいときだけ API が1回に減らす
  const expected = mode === "none" ? 1 : mode === "client" ? (client?.count ?? 1) : plan.count;
  const data = q.data && !q.isPlaceholderData ? q.data : undefined;
  const shown = data ? (data.rows.length > 1 ? data.rows.length : 0) : expected > 1 ? expected : 0;
  const rows = Array.from({ length: shown }, (_, i) => data?.rows[i] ?? { seq: i + 1, dueOn: "", amount: null as number | null });
  return (
    <div className="issue-inst" data-testid="issue-installments">
      <div className="row" style={{ gap: 6, alignItems: "center", marginBottom: 8 }}>
        <span id="is_inst_l" style={{ fontSize: "var(--fs-15)", color: "var(--ink2)", fontWeight: 700 }}>{PAY_SCHEDULE_MSG.title}</span>
        <Help testId="help-issue-installments" label={PAY_SCHEDULE_MSG.title} text={t(PAY_SCHEDULE_MSG.help)} />
      </div>
      <Segmented
        labelledBy="is_inst_l"
        options={
          client
            ? [
                { value: "client" as const, label: t(PAY_SCHEDULE_MSG.choiceClient(installmentPlanLabel(client))), testId: "is-inst-client" },
                { value: "custom" as const, label: PAY_SCHEDULE_MSG.choiceCustom, testId: "is-inst-custom" },
                { value: "none" as const, label: PAY_SCHEDULE_MSG.choiceNone, testId: "is-inst-none" },
              ]
            : [
                { value: "client" as const, label: PAY_SCHEDULE_MSG.choiceNone, testId: "is-inst-none" },
                { value: "custom" as const, label: PAY_SCHEDULE_MSG.choiceCustom, testId: "is-inst-custom" },
              ]
        }
        value={mode}
        onChange={set}
      />
      {mode === "custom" ? (
        <div className="row" style={{ gap: 12, flexWrap: "wrap", marginTop: 10 }}>
          {sel("is_inst_count", PAY_SCHEDULE_MSG.count, plan.count, INSTALLMENT_COUNTS, PAY_SCHEDULE_MSG.countLabel, (n) => setP({ ...plan, count: n }))}
          {sel("is_inst_interval", PAY_SCHEDULE_MSG.interval, plan.intervalMonths, INSTALLMENT_INTERVALS, intervalLabel, (n) => setP({ ...plan, intervalMonths: n }))}
        </div>
      ) : null}
      {rows.length ? (
        <table className="list psched-table issue-inst-table" data-testid="issue-installments-table">
          <thead>
            <tr>
              <th>{PAY_SCHEDULE_MSG.colSeq}</th>
              <th>{PAY_SCHEDULE_MSG.colDue}</th>
              <th className="r">{PAY_SCHEDULE_MSG.colAmount}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.seq} data-testid="issue-installments-row" aria-busy={r.amount == null || undefined}>
                <td>{PAY_SCHEDULE_MSG.seqLabel(r.seq)}</td>
                <td className="num">{r.dueOn ? ymdSlash(r.dueOn) : ""}</td>
                <td className="num r">{r.amount == null ? "" : yen(r.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
